// disOS document store: a Start tab plus any number of open cases kept in memory, each autosaved to the shared API.
// Closing a tab keeps the case saved on the server. UI state (tabs, selection, dialogs) is global, like a desktop app.
import { create } from 'zustand';
import { normalizeCase, type Case, type TaxYear } from '@divorceos/schema';
import { api, clientsApi, type CaseRecord, type ClientDetail, type ClientRecord } from '../api/client';
import { newDisCase } from './model';
import { migrateFromStorage, prefsOf, readDisAppData, saveExpanded, splitPrefs, writeDisAppData, type CasePrefs } from './appData';
import { useAuth } from '../auth/authStore';

export type SaveStatus = 'new' | 'dirty' | 'saving' | 'saved' | 'error';

export interface Doc {
  /** Stable tab key: the case id, or "new-N" until the first save. */
  key: string;
  caseId: string | null;
  data: Case;
  name: string;
  /** disOS versioning: the client (folder) this scenario belongs to, and its label. */
  clientId: string | null;
  label: string;
  saveStatus: SaveStatus;
  saveError: string | null;
  updatedAt?: string;
  /** disOS data stored with the case (case.appData.disOS) plus the browser-only grid expansion; derived from `data`. */
  prefs: CasePrefs;
  /** The inputs the results were last computed from (differs from `data` while auto-recalculate is off). */
  calcData: Case;
  calcAt: number;
}

export type LeftTab = 'data' | 'children' | 'support' | 'taxes' | 'parties' | 'qbid' | 'taxdata' | 'sensitivity' | 'reports';
export type RightTab = 'results' | 'cash' | 'notes' | 'help';
export type DialogKind = 'settings' | 'about' | 'forms' | 'compare' | 'worksheet' | 'open' | 'questionnaire' | 'import' | null;
export type SettingsTab = 'cs' | 'ss' | 'tax' | 'tac' | 'info' | 'prog';

export interface Ui {
  left: LeftTab;
  right: RightTab;
  sel: string;
  helpTopic: string | null;
  report: string;
  checked: Record<string, boolean>;
  zoom: 'fit' | '100';
  autoRecalc: boolean;
  tooltips: boolean;
  hideZeroGrid: boolean;
  /** C21: Excel-style highlight of the focused parent column (and row) in the Case Data grid. */
  crossHighlight: boolean;
  /** C29: the onboarding coach-mark tour is open. */
  tourOpen: boolean;
  /** Third-portion Summary pane: whether it's shown, and its draggable width (px). */
  summaryOpen: boolean;
  summW: number;
  /** Inputs pane share of the Inputs+Results region, as a percentage (default 57; the rest goes to Results). */
  leftFr: number;
  /** Case Data grid column widths as percentages: the Input (label) column and the first party; the second
   *  party column takes the remainder. Draggable at the header boundaries. */
  gridLabelW: number;
  gridFatherW: number;
  menu: number | null;
  dialog: DialogKind;
  dialogArg: number;
  settingsTab: SettingsTab;
  formParty: 0 | 1;
  toast: string | null;
  /** Report ids being printed (rendered into #print-root, then window.print()). */
  print: string[] | null;
  gOpen: boolean;
  pOpen: boolean;
  cfOpen: boolean;
  /** Preview mode overlay open (for the active case). */
  preview: boolean;
  /** Last preview tab per open case (doc key). In memory only: a tab click is not a case edit and must not autosave. */
  previewTab: Record<string, string>;
  /** Tax-form tabs in Preview mode: party and scenario (g = Guideline, p = Proposed). */
  previewParty: 0 | 1;
  previewScenario: 'g' | 'p';
}

interface DisState {
  docs: Doc[];
  active: string;
  ui: Ui;
  recent: CaseRecord[];
  recentError: string | null;
  recentLoading: boolean;
  /** Cache of clients (folders) with their scenarios, keyed by client id. */
  clientCache: Record<string, ClientDetail>;
  /** Clients (folders) for the Start page, newest-activity first. */
  clientsList: ClientRecord[];
  clientsLoading: boolean;
  clientsError: string | null;

  refreshRecent(): Promise<void>;
  refreshClients(): Promise<void>;
  newCase(year?: TaxYear): string;
  openCase(id: string): Promise<string | null>;
  /** Create a new blank scenario, optionally inside an existing client; opens it. */
  newScenario(opts?: { clientId?: string; year?: TaxYear; label?: string }): Promise<string | null>;
  /** Duplicate a scenario (saved first) into the same client; opens the copy. */
  duplicateScenario(key?: string, label?: string): Promise<string | null>;
  /** Rename a scenario (by its case id) and refresh the client. */
  renameScenario(scenarioId: string, label: string, clientId?: string): Promise<void>;
  /** Delete a scenario by id (and its client if it was the last one); moves to another scenario. */
  deleteScenario(scenarioId: string, clientId?: string): Promise<void>;
  /** Delete a whole client (folder) and every scenario in it. */
  deleteClient(clientId: string): Promise<void>;
  /** Switch to another scenario of the current client, reusing the tab (replace-in-place). */
  switchScenario(scenarioId: string): Promise<void>;
  /** Persist a new order for a client's scenarios (array of ids in the new order). */
  reorderScenarios(clientId: string, orderedIds: string[]): Promise<void>;
  /** Persist a new order for the client list (array of client ids in the new order). */
  reorderClients(orderedIds: string[]): Promise<void>;
  /** Load (and cache) a client with its ordered scenarios for the scenario bar. */
  loadClient(id: string, force?: boolean): Promise<void>;
  /** Rename a client (the folder). */
  renameClient(id: string, name: string): Promise<void>;
  activate(key: string): void;
  closeDoc(key: string): void;
  update(fn: (c: Case) => void): void;
  setName(name: string): void;
  setPrefs(fn: (p: CasePrefs) => void): void;
  save(key?: string): Promise<void>;
  recalc(): void;
  setUi(patch: Partial<Ui>): void;
  toast(msg: string): void;
}

let untitled = 0;
const timers = new Map<string, ReturnType<typeof setTimeout>>();
let toastTimer: ReturnType<typeof setTimeout> | null = null;

const DEFAULT_CHECKED: Record<string, boolean> = { bench: true, releases: false, compare: false, smith: false };

/** C25: seed a brand-new case with the user's default party-label scheme (when they've set one other than Auto). */
function withFirmLabelDefault(data: Case): Case {
  const pref = useAuth.getState().firm?.labelPref;
  if (pref && pref !== 'auto') {
    const d = readDisAppData(data);
    if (d.labels === 'auto') { d.labels = pref as typeof d.labels; writeDisAppData(data, d); }
  }
  return data;
}

/** Summary pane width clamp (px) and inputs-share clamp (%). */
export const clampSummW = (n: number): number => Math.min(560, Math.max(240, Math.round(n)));
export const clampFr = (n: number): number => Math.min(78, Math.max(28, Math.round(n)));
export interface LayoutState { summaryOpen: boolean; summW: number; leftFr: number; gridLabelW: number; gridFatherW: number }
const LAYOUT_KEY = 'disos.layout';
/** Load the saved layout (Summary pane + the inputs/results split + the grid column widths); else defaults. */
function loadLayout(): LayoutState {
  const d: LayoutState = { summaryOpen: true, summW: 320, leftFr: 57, gridLabelW: 46, gridFatherW: 27 };
  try {
    const r = JSON.parse(localStorage.getItem(LAYOUT_KEY) || '{}');
    return {
      summaryOpen: typeof r.summaryOpen === 'boolean' ? r.summaryOpen : d.summaryOpen,
      summW: Number.isFinite(r.summW) ? clampSummW(r.summW) : d.summW,
      leftFr: Number.isFinite(r.leftFr) ? clampFr(r.leftFr) : d.leftFr,
      gridLabelW: Number.isFinite(r.gridLabelW) ? r.gridLabelW : d.gridLabelW,
      gridFatherW: Number.isFinite(r.gridFatherW) ? r.gridFatherW : d.gridFatherW,
    };
  } catch { return d; }
}
export function saveLayout(l: LayoutState): void {
  try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(l)); } catch { /* ignore */ }
}

export const useDis = create<DisState>((set, get) => ({
  docs: [],
  active: 'start',
  ui: {
    left: 'data', right: 'results', sel: 'wages', helpTopic: null, report: 'bench', checked: { ...DEFAULT_CHECKED }, zoom: 'fit',
    autoRecalc: true, tooltips: true, hideZeroGrid: false, crossHighlight: true, tourOpen: false, menu: null, dialog: null, dialogArg: 0, settingsTab: 'cs', formParty: 0, toast: null,
    print: null, gOpen: true, pOpen: true, cfOpen: false, preview: false, previewTab: {}, previewParty: 0, previewScenario: 'g',
    ...loadLayout(),
  },
  recent: [],
  recentError: null,
  recentLoading: false,
  clientCache: {},
  clientsList: [],
  clientsLoading: false,
  clientsError: null,

  async refreshRecent() {
    set({ recentLoading: true });
    try {
      const list = await api.listCases();
      list.sort((a, b) => String(b.updatedAt ?? '').localeCompare(String(a.updatedAt ?? '')));
      set({ recent: list, recentError: null, recentLoading: false });
      // Keep client names available for the Recent-cases menu and tab/title labels.
      void get().refreshClients();
    } catch (e) {
      set({ recentError: e instanceof Error ? e.message : String(e), recentLoading: false });
    }
  },

  async refreshClients() {
    set({ clientsLoading: true });
    try {
      const list = await clientsApi.list();
      set({ clientsList: list, clientsError: null, clientsLoading: false });
    } catch (e) {
      set({ clientsError: e instanceof Error ? e.message : String(e), clientsLoading: false });
    }
  },

  newCase(year) {
    untitled += 1;
    const key = `new-${untitled}`;
    const data = withFirmLabelDefault(newDisCase(year ?? 2025));
    const doc: Doc = { key, caseId: null, data, name: `Untitled ${untitled}`, clientId: null, label: '', saveStatus: 'new', saveError: null, prefs: prefsOf(data, {}), calcData: data, calcAt: Date.now() };
    set((s) => ({ docs: [...s.docs, doc], active: key, ui: { ...s.ui, left: 'data', right: 'results', menu: null } }));
    return key;
  },

  async openCase(id) {
    const open = get().docs.find((d) => d.caseId === id || d.key === id);
    if (open) { get().activate(open.key); return open.key; }
    try {
      const rec = await api.getCase(id);
      const doc = docFromRecord(rec);
      set((s) => (s.docs.some((d) => d.key === rec.id) ? { active: rec.id } : { docs: [...s.docs, doc], active: rec.id, ui: { ...s.ui, left: s.ui.left === 'reports' ? 'data' : s.ui.left, menu: null } }));
      if (doc.saveStatus === 'dirty') schedule(rec.id);
      return rec.id;
    } catch (e) {
      get().toast(`Could not open the case: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }
  },

  async newScenario(opts) {
    try {
      const data = withFirmLabelDefault(newDisCase(opts?.year ?? 2025));
      const rec = await api.createScenario({ data, clientId: opts?.clientId, label: opts?.label });
      const doc = docFromRecord(rec);
      set((s) => ({ docs: [...s.docs, doc], active: doc.key, ui: { ...s.ui, left: 'data', right: 'results', menu: null } }));
      if (rec.clientId) void get().loadClient(rec.clientId, true);
      void get().refreshRecent();
      return doc.key;
    } catch (e) {
      get().toast(`Could not create the scenario: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }
  },

  async duplicateScenario(key, label) {
    const k = key ?? get().active;
    const doc = get().docs.find((d) => d.key === k);
    if (!doc) return null;
    try {
      // Make sure the source is saved so the server clones the latest data.
      if (!doc.caseId || doc.saveStatus === 'dirty' || doc.saveStatus === 'new') await get().save(k);
      const src = get().docs.find((d) => d.key === k);
      if (!src?.caseId) { get().toast('Save the scenario before duplicating it.'); return null; }
      const copyLabel = label ?? `${src.label || src.name || 'Scenario'} (copy)`;
      const rec = await api.createScenario({ cloneFrom: src.caseId, clientId: src.clientId ?? undefined, label: copyLabel });
      const nd = docFromRecord(rec);
      set((s) => ({ docs: [...s.docs, nd], active: nd.key, ui: { ...s.ui, menu: null } }));
      if (rec.clientId) void get().loadClient(rec.clientId, true);
      void get().refreshRecent();
      get().toast(`Duplicated as “${nd.label}”.`);
      return nd.key;
    } catch (e) {
      get().toast(`Could not duplicate: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }
  },

  async renameScenario(scenarioId, label, clientId) {
    try {
      const rec = await api.setScenarioMeta(scenarioId, { label });
      // Reflect the new label on the open tab, if this scenario is open.
      const open = get().docs.find((d) => d.caseId === scenarioId);
      if (open) patchDoc(open.key, { label: rec.label ?? label });
      if (clientId) await get().loadClient(clientId, true);
    } catch (e) {
      get().toast(`Could not rename: ${e instanceof Error ? e.message : String(e)}`);
    }
  },

  async deleteScenario(scenarioId, clientId) {
    try {
      await api.deleteCase(scenarioId);
      const open = get().docs.find((d) => d.caseId === scenarioId);
      const wasActive = open && get().active === open.key;
      if (clientId) await get().loadClient(clientId, true);
      const remaining = clientId ? (get().clientCache[clientId]?.scenarios ?? []) : [];
      if (open) get().closeDoc(open.key);
      // If we closed the active scenario and the client still has others, jump to one.
      if (wasActive && remaining.length > 0 && !get().docs.some((d) => d.clientId === clientId)) {
        void get().openCase(remaining[0].id);
      }
      get().toast('Scenario deleted.');
    } catch (e) {
      get().toast(`Could not delete: ${e instanceof Error ? e.message : String(e)}`);
    }
  },

  async deleteClient(clientId) {
    try {
      await get().loadClient(clientId, true);
      const scn = get().clientCache[clientId]?.scenarios ?? [];
      for (const s of scn) {
        await api.deleteCase(s.id);
        const open = get().docs.find((d) => d.caseId === s.id);
        if (open) get().closeDoc(open.key);
      }
      await get().refreshClients();
      get().toast(scn.length === 1 ? 'Client deleted.' : `Client and ${scn.length} scenarios deleted.`);
    } catch (e) {
      get().toast(`Could not delete: ${e instanceof Error ? e.message : String(e)}`);
    }
  },

  async switchScenario(scenarioId) {
    const s0 = get();
    const cur = s0.docs.find((d) => d.key === s0.active) ?? null;
    if (cur?.caseId === scenarioId) return;               // already viewing it
    const key = await s0.openCase(scenarioId);            // opens or activates the target
    if (!key) return;
    const target = get().docs.find((d) => d.key === key);
    // Replace-in-place: close the tab we came from when it's a sibling scenario (same client).
    if (cur && target && cur.key !== target.key && cur.clientId && cur.clientId === target.clientId) {
      get().closeDoc(cur.key);
    }
  },

  async reorderScenarios(clientId, orderedIds) {
    try {
      await Promise.all(orderedIds.map((id, i) => api.setScenarioMeta(id, { order: i })));
      await get().loadClient(clientId, true);
    } catch (e) {
      get().toast(`Could not reorder: ${e instanceof Error ? e.message : String(e)}`);
    }
  },

  async reorderClients(orderedIds) {
    // Optimistic: reflect the new order immediately, then persist.
    set((s) => {
      const pos = new Map(orderedIds.map((id, i) => [id, i] as const));
      const list = [...s.clientsList].sort((a, b) => (pos.get(a.id) ?? 0) - (pos.get(b.id) ?? 0));
      return { clientsList: list };
    });
    try {
      await Promise.all(orderedIds.map((id, i) => clientsApi.update(id, { order: i })));
      await get().refreshClients();
    } catch (e) {
      get().toast(`Could not reorder clients: ${e instanceof Error ? e.message : String(e)}`);
      void get().refreshClients();
    }
  },

  async loadClient(id, force) {
    if (!id) return;
    if (!force && get().clientCache[id]) return;
    try {
      const detail = await clientsApi.get(id);
      set((s) => ({ clientCache: { ...s.clientCache, [id]: detail } }));
    } catch { /* leave any cached copy in place; the bar degrades gracefully */ }
  },

  async renameClient(id, name) {
    // Optimistic: update the caches that the tab/title read from, so the rename shows immediately.
    set((s) => ({
      clientCache: s.clientCache[id] ? { ...s.clientCache, [id]: { ...s.clientCache[id], name } } : s.clientCache,
      clientsList: s.clientsList.map((c) => (c.id === id ? { ...c, name } : c)),
    }));
    try {
      await clientsApi.update(id, { name });
      await get().loadClient(id, true);
      void get().refreshClients();
    } catch (e) {
      get().toast(`Could not rename the client: ${e instanceof Error ? e.message : String(e)}`);
      void get().refreshClients();
    }
  },

  activate(key) {
    const prev = get().active;
    if (prev !== key) flush(prev);
    set((s) => ({ active: key, ui: { ...s.ui, menu: null, preview: false } }));
  },

  closeDoc(key) {
    flush(key);
    set((s) => {
      const i = s.docs.findIndex((d) => d.key === key);
      if (i < 0) return {};
      const docs = s.docs.filter((d) => d.key !== key);
      const active = s.active === key ? (docs[Math.max(0, i - 1)]?.key ?? 'start') : s.active;
      return { docs, active, ui: s.active === key ? { ...s.ui, preview: false } : s.ui };
    });
    void get().refreshRecent();
  },

  update(fn) {
    const key = get().active;
    set((s) => ({
      docs: s.docs.map((d) => {
        if (d.key !== key) return d;
        const data = structuredClone(d.data);
        fn(data);
        const calc = s.ui.autoRecalc ? { calcData: data, calcAt: Date.now() } : {};
        return { ...d, data, prefs: prefsOf(data, d.prefs.expanded), ...calc, saveStatus: 'dirty' };
      }),
    }));
    schedule(key);
  },

  setName(name) {
    const key = get().active;
    set((s) => ({ docs: s.docs.map((d) => (d.key === key ? { ...d, name, saveStatus: 'dirty' } : d)) }));
    schedule(key);
  },

  setPrefs(fn) {
    const key = get().active;
    let dirty = false;
    set((s) => ({
      docs: s.docs.map((d) => {
        if (d.key !== key) return d;
        const next = structuredClone(d.prefs);
        fn(next);
        const { app, expanded } = splitPrefs(next);
        const before = splitPrefs(d.prefs);
        if (JSON.stringify(expanded) !== JSON.stringify(before.expanded)) saveExpanded(d.caseId ?? d.key, expanded);
        if (JSON.stringify(app) === JSON.stringify(before.app)) return { ...d, prefs: next };
        // Case-stored part: write into case.appData.disOS and autosave. appData is not an engine input, so the
        // calculated copy follows without marking the results stale.
        dirty = true;
        const data = structuredClone(d.data);
        writeDisAppData(data, app);
        const calcData = d.calcData === d.data ? data : { ...d.calcData, appData: data.appData };
        return { ...d, data, calcData, prefs: prefsOf(data, expanded), saveStatus: 'dirty' };
      }),
    }));
    if (dirty) schedule(key);
  },

  async save(key) {
    const k = key ?? get().active;
    const doc = get().docs.find((d) => d.key === k);
    if (!doc) return;
    const t = timers.get(k);
    if (t) { clearTimeout(t); timers.delete(k); }
    patchDoc(k, { saveStatus: 'saving', saveError: null });
    try {
      const nm = doc.name.trim() === '' ? undefined : doc.name.trim();
      const rec = doc.caseId ? await api.updateCase(doc.caseId, nm, doc.data) : await api.createCase(nm, doc.data);
      const cur = get().docs.find((d) => d.key === k);
      if (!doc.caseId) saveExpanded(rec.id, (cur?.prefs ?? doc.prefs).expanded);
      patchDoc(k, {
        caseId: rec.id, updatedAt: rec.updatedAt, name: rec.name ?? doc.name,
        clientId: rec.clientId ?? doc.clientId, label: doc.label || rec.label || '',
        saveStatus: cur && cur.data !== doc.data ? 'dirty' : 'saved',
      });
      // A brand-new case just created its client: load it so the scenario bar fills in.
      if (!doc.caseId && rec.clientId) void get().loadClient(rec.clientId, true);
      if (cur && cur.data !== doc.data) schedule(k);
    } catch (e) {
      patchDoc(k, { saveStatus: 'error', saveError: e instanceof Error ? e.message : String(e) });
    }
  },

  recalc() {
    const key = get().active;
    set((s) => ({ docs: s.docs.map((d) => (d.key === key ? { ...d, calcData: d.data, calcAt: Date.now() } : d)) }));
  },

  setUi(patch) { set((s) => ({ ui: { ...s.ui, ...patch } })); },

  toast(msg) {
    if (toastTimer) clearTimeout(toastTimer);
    set((s) => ({ ui: { ...s.ui, toast: msg } }));
    toastTimer = setTimeout(() => set((s) => ({ ui: { ...s.ui, toast: null } })), 3200);
  },
}));

function patchDoc(key: string, patch: Partial<Doc>): void {
  useDis.setState((s) => ({ docs: s.docs.map((d) => (d.key === key ? { ...d, ...patch } : d)) }));
}

/** Build an open document from a saved case record (shared by open/new/duplicate). */
function docFromRecord(rec: CaseRecord): Doc {
  // Browser-only data from earlier builds moves into case.appData.disOS once (then saved like any edit).
  const m = migrateFromStorage(rec.id, normalizeCase(rec.data));
  const data = m.data;
  return {
    key: rec.id, caseId: rec.id, data, name: rec.name ?? '',
    clientId: rec.clientId ?? null, label: rec.label ?? '',
    saveStatus: m.migrated ? 'dirty' : 'saved', saveError: null, updatedAt: rec.updatedAt,
    prefs: prefsOf(data, m.expanded), calcData: data, calcAt: Date.now(),
  };
}

/** Autosave one second after the last edit (new cases are created on the server on their first edit). */
function schedule(key: string): void {
  const t = timers.get(key);
  if (t) clearTimeout(t);
  timers.set(key, setTimeout(() => {
    timers.delete(key);
    const d = useDis.getState().docs.find((x) => x.key === key);
    if (d && (d.saveStatus === 'dirty' || d.saveStatus === 'error')) void useDis.getState().save(key);
  }, 1000));
}

/** Save a pending edit now (tab switch, close, page hide). */
export function flush(key: string): void {
  const t = timers.get(key);
  if (!t) return;
  clearTimeout(t);
  timers.delete(key);
  const d = useDis.getState().docs.find((x) => x.key === key);
  if (d && d.saveStatus === 'dirty') void useDis.getState().save(key);
}

export function flushAll(): void {
  for (const key of [...timers.keys()]) flush(key);
}

/** The active case document, or null on the Start tab. */
export function useActiveDoc(): Doc | null {
  return useDis((s) => s.docs.find((d) => d.key === s.active) ?? null);
}
